import { Injectable, OnDestroy } from '@angular/core';
import {
   AbstractControl,
   UntypedFormBuilder,
   UntypedFormGroup,
   ValidationErrors,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ExtensionTypes, NullActionTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { AutoAttendantKeyItemView } from 'src/app/common-components/auto-attendant-key-list/models/auto-attendant-key-item.model';
import { ChipInputType } from 'src/app/common-components/entity-select/extension-and-extension-group/chip-input/extension-and-group.model';
import {
   ExtensionChipInputOption,
   ExtensionChipInputType,
} from 'src/app/common-components/entity-select/extension/chip-input/extension-phone-number.model';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { trimPhoneNumber } from 'src/app/common-components/helpers/trim-phone-number-helper';
import { combinedAreaCodeValidator } from 'src/app/common-components/validators/area-code.validator';
import { containsCharactersValidator } from 'src/app/common-components/validators/contain-characters.validator';
import { phoneNumberValidator } from 'src/app/common-components/validators/phone-number.validator';
import { phoneIntegerValidator } from 'src/app/common-components/validators/value-integer.validator';
import { ExtensionOption } from '../../extension-register/models/extension-option.interface';
import { getNullActionPlaceholder, getNullActionTypeNumericValue } from '../helpers/null-action-helper';
import { DepartmentExtensionItemView } from '../models/department-extension.model';
import { ExtensionDetailFormValue } from '../models/extension-detail-form';
import { ExtensionDetailSaveModel } from '../models/extension-detail-save.model';
import { ExtensionDetailView } from '../models/extension-detail-view.model';
import { GetExtensionDetail_extension_extensionQueue } from '../queries/generated/GetExtensionDetail';
import { FaxEmailItemView } from '../views/extension-fax-sections/fax-emails-tab/models/fax-emails-item-view';
import { FindMeFollowMeStepItemView } from '../views/extension-find-me-follow-me-sections/models/extension-find-me-follow-me-step.model';

@Injectable()
export class ExtensionDetailFormService extends FormsBase implements OnDestroy {
   private destroyed$ = new Subject<void>();

   constructor(private fb: UntypedFormBuilder, private store: Store, private action: Actions) {
      super();

      this.form = this.buildForm();
   }
   resetForm(): void {
      this.form = this.buildForm();
   }

   private buildForm(): UntypedFormGroup {
      return this.fb.group({
         extensionAutoAttendant: this.fb.group(
            {
               autoAttendantKeys: this.fb.control([]),
               autoAttendantPrompts: this.fb.control([]),
               extensionAutoAttendantId: this.fb.control(null),
               useTimeoutToggle: this.fb.control(false),
               timeoutExtension: this.fb.control(null),
               timeoutSeconds: this.fb.control(null),
               timeoutNullAction: this.fb.control(null),
               firstPromptNotSkippable: this.fb.control(null),
               isTimeoutVoicemailOnly: this.fb.control(null),
            },
            { validators: this.timeoutExtensionValidator }
         ),
         extensionFindMeFollowMe: this.fb.group({
            extensionFmFmId: this.fb.control(null),
            extensionId: this.fb.control(null),
            finalDestinationExtension: this.fb.control(null),
            isFinalDestinationVoicemail: this.fb.control(null),
            isIgnoreForward: this.fb.control(null),
            isGreetingDisabled: this.fb.control(null),
            isSendOriginalCallerId: this.fb.control(null),
            finalDestinationPhoneNumber: this.fb.control(null),
            defaultPhoneNumber: this.fb.control(null),
            fmFmSteps: this.fb.control([]),
         }),
         extensionExternalForward: this.fb.group({
            dtmf: this.fb.control(null),
            extensionExternalForwardId: this.fb.control(null),
            forwardToNumber: this.fb.control(null),
            isDtmf: this.fb.control(null),
            isPassthroughOriginalCallerId: this.fb.control(null),
            pauseSeconds: this.fb.control(null),
         }),
         extensionHuntGroup: this.fb.group({
            callerIdOverride: this.fb.control(null),
            displayName: this.fb.control(null),
            extensionHuntGroupId: null,
            huntGroupMembers: this.fb.control(null),
            huntGroupExtensionGroupMembers: this.fb.control(null),
            ringTimeout: this.fb.control(null),
            routeBusyExtension: this.fb.control(null),
            routeBusyNullAction: this.fb.control(null),
            routeNoAnswerExtension: this.fb.control(null),
            isRouteBusyVoicemailOnly: this.fb.control(null),
            isRouteNoAnswerVoicemailOnly: this.fb.control(null),
            routeNoAnswerNullAction: this.fb.control(null),
            extensionsOrExtensionGroupMembers: this.fb.control({
               huntGroupMembers: [[]],
               huntGroupExtensionGroups: [[]],
            }),
         }),
         extensionMailbox: this.fb.group({
            customerNotes: this.fb.control(null),
            extensionMailboxId: this.fb.control(null),
            isInNameDirectory: this.fb.control(null),
            notes: this.fb.control(null),
         }),
         extensionPhone: this.fb.group({
            defaultPhoneNumber: this.fb.control(null),
            dndStatus: this.fb.control(null, Validators.maxLength(50)),
            displayName: this.fb.control(null),
            extensionPhoneId: this.fb.control(null),
            defaultAreaCode: this.fb.control(null, combinedAreaCodeValidator()),
            forwardToExtension: this.fb.control(null),
            forwardExternalNumber: this.fb.control(null),
            phones: this.fb.control([]),
            ringTimeout: this.fb.control(null),
            enableDtmf: this.fb.control(null),
            secondsToWait: this.fb.control(null),
            dtmfToPlay: this.fb.control(null),
            isOtrEnabled: this.fb.control(null),
            routeBusyExtension: this.fb.control(null),
            routeBusyNullAction: this.fb.control(null),
            routeNoAnswerExtension: this.fb.control(null),
            routeNoAnswerNullAction: this.fb.control(null),
            isInNameDirectory: this.fb.control(null),
            extensionSupervisees: this.fb.control([]),
            extensionGroupSupervisees: this.fb.control([]),
            sipUsernames: this.fb.control([], this.uniqueSipUserNameValidator()),
            isAutoAnswer: this.fb.control(null),
            isRingAllPhones: this.fb.control(null),
            autoAnswerPhone: this.fb.control(null),
            isAutoAnswerEnabled: this.fb.control(null),
            office: this.fb.control(null, [containsCharactersValidator(), Validators.maxLength(255)]),
            extensionsOrExtensionGroupSupervisees: this.fb.control({
               extensionSupervisees: [[]],
               extensionGroupSupervisees: [[]],
            }),
         }),
         extensionPagingGroup: this.fb.group({
            extensionPagingGroupId: null,
            pagingGroupMembers: this.fb.control(null),
            pagingGroupExtensionGroups: this.fb.control(null),
            pagingGroupMembersOrExtensionGroupMembers: this.fb.control({
               pagingGroupMembers: [[]],
               pagingGroupExtensionGroups: [[]],
            }),
         }),
         extensionQueue: this.fb.group(
            {
               announcementFrequency: this.fb.control(null),
               callbackPrompt: this.fb.control(null),
               callersNeededForCallback: this.fb.control(null),
               typeOfCallback: this.fb.control(null),
               holdTimeNeededForCallback: this.fb.control(null),
               exitRouteToExtension: this.fb.control(null),
               exitRouteVoicemailOnly: this.fb.control(null),
               hasPreventEmptyQueue: this.fb.control(null),
               queueEmptyExitRouteToExtension: this.fb.control(null),
               queueEmptyExitNullAction: this.fb.control(null),
               extensionQueueId: null,
               holdMusicPrompt: this.fb.control(null),
               isAnnounceQueueCount: this.fb.control(null),
               isAnnounceCallback: this.fb.control(null),
               isAnnounceWaitTime: this.fb.control(null),
               isCallBackAvailable: this.fb.control(null),
               periodicAnnouncementFrequency: this.fb.control(null),
               queueMembers: this.fb.control([]),
               queueGroupExtensionGroups: this.fb.control([]),
               queuePeriodicAnnouncements: this.fb.control([]),
               queueStrategyId: this.fb.control(null),
               wrapUpTime: this.fb.control(null),
               extensionsOrExtensionQueueGroupMembers: this.fb.control({
                  queueMembers: [[]],
                  queueGroupExtensionGroups: [[]],
               }),
            },
            { validators: this.queueEmptyExitValidator }
         ),
         extensionFax: this.fb.group({
            defaultPhoneNumber: this.fb.control(null),
            extensionFaxId: this.fb.control(null),
            faxEmailAddresses: this.fb.control([]),
            emailAddress: this.fb.control(null, [
               Validators.pattern(
                  /^\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(,\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}))*\s*$/i
               ),
            ]),
         }),
         accountId: this.fb.control(null),
         location: this.fb.control(null),
         adminNote: this.fb.control(null),
         extensionNote: this.fb.control(null),
         adminNotes: this.fb.control(null),
         extensionNotes: this.fb.control(null),
         mailbox: this.fb.group({
            emailAddress: this.fb.control(
               null,
               Validators.pattern(
                  /^\s*(([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(,\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}))*)?\s*$/i
               )
            ),
            discardAudioAfterEmail: this.fb.control(null),
            sayCallerId: this.fb.control(null),
            markReadAfterEmail: this.fb.control(null),
            isInNameDirectory: this.fb.control(null),
            unavailablePrompt: this.fb.control(null),
            temporaryUnavailablePrompt: this.fb.control(null),
            useTemporaryGreeting: this.fb.control(false),
            mailboxId: this.fb.control(null),
            namePrompt: this.fb.control(null),
            operatorExtension: this.fb.control(null),
            password: this.fb.control(null),
            busyPrompt: this.fb.control(null),
         }),
         mailboxId: this.fb.control(null),
         name: this.fb.control(null),
         shortName: this.fb.control(null),
         number: this.fb.control(null),
         timeBasedRoutes: this.fb.control([]),
         fmFmSteps: this.fb.control([]),
         timeZone: this.fb.control(null),
         type: this.fb.control(null),
         users: this.fb.control(null),
         faxEmailAddresses: this.fb.control([]),
         departmentExtensions: this.fb.control([]),
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   updateExtension(extension: ExtensionDetailView): void {
      if (!extension) {
         return;
      }

      let formValue: { [key: string]: any } = {
         accountId: extension.account?.accountId,
         location: extension.location,
         adminNote: extension?.adminNote ?? null,
         adminNotes: extension.adminNote?.notes,
         extensionNote: extension?.extensionNote ?? null,
         extensionNotes: extension.extensionNote?.notes,
         mailboxId: extension.mailboxId,
         name: extension.name,
         shortName: extension.shortName,
         number: extension.number,
         timeZone: { timeZoneId: extension?.timeZoneId ?? null },
         timeBasedRoutes: extension.timeBasedRoutesConnection?.items ?? null,
         type: extension.type,
         users: extension.usersConnection?.items.map((user) => {
            return {
               name: user.name,
               userId: user.userId,
            };
         }),
         faxEmailAddresses:
            extension.faxEmailAddressesConnection?.items.map((email) => {
               return {
                  emailAddress: email.emailAddress,
                  extensionId: email.extensionId,
                  faxEmailAddressId: email.faxEmailAddressId,
               };
            }) ?? [],
         departmentExtensions: extension.departmentExtensionsConnection?.items.map((departmentExtension) => {
            return {
               departmentExtensionId: departmentExtension.departmentExtensionId,
               extensionId: departmentExtension.extensionId,
               departmentId: departmentExtension.departmentId,
               department: {
                  departmentId: departmentExtension.department?.departmentId,
                  name: departmentExtension.department?.name,
                  isActive: departmentExtension.department?.isActive,
                  accountId: departmentExtension.department?.accountId,
               },
               isDeleted: false,
            };
         }),
      };
      switch (extension.type?.name) {
         case ExtensionTypes.AutoAttendant:
            this.setAutoAttendantValidators();
            formValue.extensionAutoAttendant = {
               autoAttendantKeys: extension.extensionAutoAttendant?.autoAttendantKeysConnection.items.map((key) => {
                  return {
                     autoAttendantKeyId: key.autoAttendantKeyId,
                     isDeleted: false,
                     keyNumber: key.keyNumber,
                     extensionAutoAttendantId: key.extensionAutoAttendantId,
                     routeToExtension:
                        key.routeToNullAction == NullActionTypes.Unset
                           ? key.routeToExtension ?? null
                           : {
                                extensionId: null,
                                name: getNullActionPlaceholder(key.routeToNullAction),
                                number: getNullActionTypeNumericValue(key.routeToNullAction),
                             },
                     voiceMailOnly: key.voiceMailOnly,
                     routeToNullAction: key.routeToNullAction,
                  };
               }),
               autoAttendantPrompts: extension.extensionAutoAttendant?.autoAttendantPromptsConnection.items,
               extensionAutoAttendantId: extension.extensionAutoAttendant?.extensionAutoAttendantId,
               timeoutExtension:
                  extension.extensionAutoAttendant?.timeoutNullAction === NullActionTypes.Unset
                     ? {
                          extensionId: extension.extensionAutoAttendant?.timeoutExtension?.extensionId,
                          name: extension.extensionAutoAttendant?.timeoutExtension?.name,
                          number: extension.extensionAutoAttendant?.timeoutExtension?.number,
                          type: extension.extensionAutoAttendant?.timeoutExtension?.type,
                       }
                     : {
                          extensionId: null,
                          name: getNullActionPlaceholder(extension.extensionAutoAttendant?.timeoutNullAction),
                          number: getNullActionTypeNumericValue(
                             extension.extensionAutoAttendant?.timeoutNullAction ?? NullActionTypes.Unset
                          ),
                       },
               timeoutSeconds: extension.extensionAutoAttendant?.timeoutSeconds ?? null,
               useTimeoutToggle: extension.extensionAutoAttendant?.timeoutSeconds !== null,
               firstPromptNotSkippable: extension.extensionAutoAttendant?.firstPromptNotSkippable ?? false,
               timeoutNullAction:
                  extension.extensionAutoAttendant?.timeoutExtension?.extensionId === -1
                     ? NullActionTypes.Directory
                     : extension.extensionAutoAttendant?.timeoutExtension?.extensionId === -2
                     ? NullActionTypes.Hangup
                     : extension.extensionAutoAttendant?.timeoutNullAction ?? NullActionTypes.Unset,
               isTimeoutVoicemailOnly: extension.extensionAutoAttendant?.isTimeoutVoicemailOnly ?? false,
            };
            break;
         case ExtensionTypes.PagingGroup:
            formValue.extensionPagingGroup = {
               extensionId: extension.extensionPagingGroup?.extensionId,
               extensionPagingGroupId: extension.extensionPagingGroup?.extensionPagingGroupId,
               pagingGroupMembers:
                  extension.extensionPagingGroup?.pagingGroupMembersConnection?.items.map((pgMember) => {
                     return {
                        extensionPagingGroupId: pgMember.extensionPagingGroupId,
                        extensionId: pgMember.extension.extensionId,
                        pagingGroupMemberId: pgMember.pagingGroupMemberId,
                        name: pgMember.extension.name,
                        number: pgMember.extension.number,
                     };
                  }) || [],
               pagingGroupExtensionGroups:
                  extension.extensionPagingGroup?.pagingGroupExtensionGroupsConnection?.items.map((pgGroup) => {
                     return {
                        pagingGroupExtensionGroupId: pgGroup.pagingGroupExtensionGroupId,
                        extensionPagingGroupId: pgGroup.extensionPagingGroupId,
                        extensionGroupId: pgGroup.extensionGroupId,
                        extensionGroup: pgGroup.extensionGroup,
                        name: pgGroup.extensionGroup.name,
                     };
                  }) || [],
               pagingGroupMembersOrExtensionGroupMembers: {
                  pagingGroupMembers:
                     extension.extensionPagingGroup?.pagingGroupMembersConnection?.items.map((item) => ({
                        pagingGroupMemberId: item.pagingGroupMemberId,
                        extensionPagingGroupId: item.extensionPagingGroupId,
                        extensionId: item.extension.extensionId,
                        extension: item.extension,
                        name: item.extension.name,
                        number: item.extension.number,
                     })) || [],
                  pagingGroupExtensionGroups:
                     extension.extensionPagingGroup?.pagingGroupExtensionGroupsConnection?.items.map((item) => ({
                        pagingGroupExtensionGroupId: item.pagingGroupExtensionGroupId,
                        pagingGroupExtensionId: item.extensionPagingGroupId,
                        extensionGroupId: item.extensionGroupId,
                        extensionGroup: item.extensionGroup,
                        name: item.extensionGroup.name,
                     })) || [],
               },
            };
            break;
         case ExtensionTypes.ExternalForward:
            this.setExternalForwardValidators();
            formValue.extensionExternalForward = {
               dtmf: extension.extensionExternalForward?.dtmf,
               extension: {
                  extensionId: extension.extensionExternalForward?.extension?.extensionId,
                  name: extension.extensionExternalForward?.extension?.name,
                  number: extension.extensionExternalForward?.extension?.number,
               },
               extensionExternalForwardId: extension.extensionExternalForward?.extensionExternalForwardId,
               forwardToNumber: extension.extensionExternalForward?.forwardToNumber,
               isDtmf: extension.extensionExternalForward?.isDtmf,
               isPassthroughOriginalCallerId: extension.extensionExternalForward?.isPassthroughOriginalCallerId,
               pauseSeconds: extension.extensionExternalForward?.pauseSeconds,
            };
            break;
         case ExtensionTypes.HuntGroup:
            this.setHuntGroupValidators();
            formValue.extensionHuntGroup = {
               callerIdOverride: extension.extensionHuntGroup?.callerIdOverride,
               displayName: extension.extensionHuntGroup?.displayName,
               extensionHuntGroupId: extension.extensionHuntGroup?.extensionHuntGroupId,
               huntGroupMembers: extension.extensionHuntGroup?.huntGroupMembersConnection.items.map((hgMember) => {
                  return {
                     extensionHuntGroupId: hgMember.extensionHuntGroupId,
                     extensionId: hgMember.extension.extensionId,
                     huntGroupMemberId: hgMember.huntGroupMemberId,
                     name: hgMember.extension.name,
                     number: hgMember.extension.number,
                  };
               }),
               huntGroupExtensionGroupMembers:
                  extension.extensionHuntGroup?.extensionGroupHuntGroupMembersConnection.items.map((hgMember) => {
                     return {
                        huntGroupExtensionGroupId: hgMember.huntGroupExtensionGroupId,
                        huntGroupExtensionId: hgMember.huntGroupExtensionId,
                        extensionGroupId: hgMember.extensionGroupId,
                        extensionGroup: hgMember.extensionGroup,
                        name: hgMember.extensionGroup.name,
                     };
                  }),
               ringTimeout: extension.extensionHuntGroup?.ringTimeout ?? null,
               routeBusyExtension:
                  extension.extensionHuntGroup?.routeBusyNullAction === NullActionTypes.Unset
                     ? {
                          extensionId: extension?.extensionHuntGroup?.routeBusyExtension?.extensionId ?? null,
                          name: extension.extensionHuntGroup?.routeBusyExtension?.name ?? '',
                          number: extension.extensionHuntGroup?.routeBusyExtension?.number,
                          type: extension.extensionHuntGroup?.routeBusyExtension?.type,
                       }
                     : {
                          extensionId: null,
                          name: getNullActionPlaceholder(extension.extensionHuntGroup?.routeBusyNullAction),
                          number: getNullActionTypeNumericValue(
                             extension.extensionHuntGroup?.routeBusyNullAction ?? NullActionTypes.Unset
                          ),
                       },
               routeBusyNullAction:
                  extension.extensionHuntGroup?.routeBusyExtension?.extensionId === -1
                     ? NullActionTypes.Directory
                     : extension.extensionHuntGroup?.routeBusyExtension?.extensionId === -2
                     ? NullActionTypes.Hangup
                     : extension.extensionHuntGroup?.routeBusyNullAction ?? NullActionTypes.Unset,
               routeNoAnswerExtension:
                  extension.extensionHuntGroup?.routeNoAnswerNullAction === NullActionTypes.Unset
                     ? {
                          extensionId: extension?.extensionHuntGroup?.routeNoAnswerExtension?.extensionId ?? null,
                          name: extension.extensionHuntGroup?.routeNoAnswerExtension?.name ?? '',
                          number: extension.extensionHuntGroup?.routeNoAnswerExtension?.number,
                          type: extension.extensionHuntGroup?.routeNoAnswerExtension?.type,
                       }
                     : {
                          extensionId: null,
                          name: getNullActionPlaceholder(extension.extensionHuntGroup?.routeNoAnswerNullAction),
                          number: getNullActionTypeNumericValue(
                             extension.extensionHuntGroup?.routeNoAnswerNullAction ?? NullActionTypes.Unset
                          ),
                       },
               routeNoAnswerNullAction:
                  extension.extensionHuntGroup?.routeNoAnswerExtension?.extensionId === -1
                     ? NullActionTypes.Directory
                     : extension.extensionHuntGroup?.routeNoAnswerExtension?.extensionId === -2
                     ? NullActionTypes.Hangup
                     : extension.extensionHuntGroup?.routeNoAnswerNullAction ?? NullActionTypes.Unset,
               isRouteBusyVoicemailOnly: extension.extensionHuntGroup?.isRouteBusyVoicemailOnly ?? false,
               isRouteNoAnswerVoicemailOnly: extension.extensionHuntGroup?.isRouteNoAnswerVoicemailOnly ?? false,
               extensionsOrExtensionGroupMembers: {
                  huntGroupMembers:
                     extension.extensionHuntGroup?.huntGroupMembersConnection?.items.map((item) => ({
                        huntGroupMemberId: item.huntGroupMemberId,
                        extensionHuntGroupId: item.extensionHuntGroupId,
                        extensionId: item.extension.extensionId,
                        extension: item.extension,
                        name: item.extension.name,
                        number: item.extension.number,
                     })) || [],
                  huntGroupExtensionGroups:
                     extension.extensionHuntGroup?.extensionGroupHuntGroupMembersConnection?.items.map((item) => ({
                        huntGroupExtensionGroupId: item.huntGroupExtensionGroupId,
                        huntGroupExtensionId: item.huntGroupExtensionId,
                        extensionGroupId: item.extensionGroupId,
                        extensionGroup: item.extensionGroup,
                        name: item.extensionGroup.name,
                     })) || [],
               },
            };
            break;
         case ExtensionTypes.SmartVoicemail:
         case ExtensionTypes.VoiceMailbox:
            this.setVoicemailboxValidators();
            formValue.extensionMailbox = {
               extension: {
                  extensionId: extension.extensionMailbox?.extension?.extensionId,
                  name: extension.extensionMailbox?.extension?.name,
                  number: extension.extensionMailbox?.extension?.number,
               },
               extensionMailboxId: extension.extensionMailbox?.extensionMailboxId,
               isInNameDirectory: extension.mailbox?.isInNameDirectory ?? true,
            };
            formValue.mailbox = {
               emailAddress: extension.mailbox?.emailAddress,
               discardAudioAfterEmail: extension.mailbox?.discardAudioAfterEmail,
               sayCallerId: extension.mailbox?.sayCallerId ?? true,
               markReadAfterEmail: extension.mailbox?.markReadAfterEmail,
               isInNameDirectory: extension.mailbox?.isInNameDirectory ?? true,
               unavailablePrompt: extension.mailbox?.unavailablePrompt,
               temporaryUnavailablePrompt: extension.mailbox?.temporaryUnavailablePrompt,
               useTemporaryGreeting: extension.mailbox?.temporaryUnavailablePromptId != null,
               mailboxId: extension.mailbox?.mailboxId,
               namePrompt: extension.mailbox?.namePrompt,
               operatorExtension: extension.mailbox?.mailboxOperatorExtension,
               password: extension.mailbox?.password ? '0000' : '',
               busyPrompt: extension.mailbox?.busyPrompt,
            };
            break;
         case ExtensionTypes.Phone:
            this.setPhoneValidators();
            formValue.extensionPhone = {
               defaultPhoneNumber: extension.extensionPhone?.defaultPhoneNumber
                  ? {
                       displayName: extension.extensionPhone.defaultPhoneNumber.displayName,
                       phoneNumber: extension.extensionPhone.defaultPhoneNumber.phoneNumber,
                       phoneNumberId: extension.extensionPhone.defaultPhoneNumber.phoneNumberId,
                    }
                  : null,
               dndStatus: extension.extensionPhone?.dndStatus,
               isOtrEnabled: (extension.accountIsOtrEnabled || extension.extensionPhone?.isOtrEnabled) ?? false,
               displayName: extension.extensionPhone?.displayName ?? extension.name,
               extensionPhoneId: extension.extensionPhone?.extensionPhoneId ?? null,
               office: extension.extensionPhone?.office ?? null,
               isAutoAnswer: extension.extensionPhone?.isAutoAnswer ?? false,
               isRingAllPhones: extension.extensionPhone?.isRingAllPhones ?? true,
               defaultAreaCode: extension.extensionPhone?.defaultAreaCode ?? null,
               autoAnswerPhone: extension.extensionPhone?.autoAnswerPhone
                  ? {
                       phoneId: extension.extensionPhone.autoAnswerPhone.phoneId,
                       phoneType: extension.extensionPhone.autoAnswerPhone.phoneType,
                       sipUsers: extension.extensionPhone.autoAnswerPhone.sipUsersConnection.items.map((sipUser) => {
                          return {
                             sipUserId: sipUser.sipUserId,
                             userName: sipUser.userName,
                          };
                       }),
                       devices: extension.extensionPhone.autoAnswerPhone.devicesConnection.items.map((device) => {
                          return {
                             deviceId: device.deviceId,
                             macAddress: device.macAddress,
                          };
                       }),
                    }
                  : null,
               isAutoAnswerEnabled: extension.extensionPhone?.isAutoAnswerEnabled ?? false,
               forwardExternalNumber: extension.extensionPhone?.forwardExternalNumber ?? null,
               forwardToExtension: {
                  extensionId: extension?.extensionPhone?.forwardExtension?.extensionId ?? null,
                  name: extension.extensionPhone?.forwardExtension?.name ?? '',
                  number: extension.extensionPhone?.forwardExtension?.number,
               },
               phones: extension.extensionPhone?.phonesConnection.items.map((phone) => {
                  return {
                     devices: phone.devicesConnection.items.map((device) => {
                        return {
                           deviceId: device.deviceId,
                           phoneId: device.phoneId,
                        };
                     }),
                     extensionId: phone.extensionId,
                     phoneId: phone.phoneId,
                     phoneType: phone.phoneType,
                     handsetIpei: phone.handsetIpei,
                     baseStationHandsets: phone.baseStationHandsetsConnection.items.map((handset) => {
                        return {
                           baseStationHandsetId: handset.baseStationHandsetId,
                           deviceId: handset.device.deviceId,
                           phoneId: phone.phoneId,
                           sortId: handset.sortId,
                        };
                     }),
                     sipUsers: phone.sipUsersConnection.items.map((sipUser) => {
                        return {
                           accountId: sipUser.accountId,
                           hashPassword: sipUser.hashPassword,
                           sipUserId: sipUser.sipUserId,
                           username: sipUser.userName,
                        };
                     }),
                  };
               }),
               ringTimeout: extension.extensionPhone?.ringTimeout ?? null,
               enableDtmf: extension.extensionPhone?.enableDtmf ?? false,
               secondsToWait: extension.extensionPhone?.secondsToWait ?? null,
               dtmfToPlay: extension.extensionPhone?.dtmfToPlay ?? null,
               isInNameDirectory: extension.mailbox?.isInNameDirectory ?? true,
               routeBusyExtension:
                  extension.extensionPhone?.routeBusyNullAction === NullActionTypes.Unset
                     ? {
                          extensionId: extension?.extensionPhone?.routeBusyExtension?.extensionId ?? null,
                          name: extension.extensionPhone?.routeBusyExtension?.name ?? '',
                          number: extension.extensionPhone?.routeBusyExtension?.number,
                       }
                     : {
                          extensionId: null,
                          name: getNullActionPlaceholder(extension.extensionPhone?.routeBusyNullAction),
                          number: getNullActionTypeNumericValue(
                             extension.extensionPhone?.routeBusyNullAction ?? NullActionTypes.Unset
                          ),
                       },
               routeBusyNullAction:
                  extension.extensionPhone?.routeBusyExtension?.extensionId === -1
                     ? NullActionTypes.Directory
                     : extension.extensionPhone?.routeBusyExtension?.extensionId === -2
                     ? NullActionTypes.Hangup
                     : extension.extensionPhone?.routeBusyExtension?.extensionId === -3
                     ? NullActionTypes.Voicemail
                     : extension.extensionPhone?.routeBusyNullAction ?? NullActionTypes.Unset,
               routeNoAnswerExtension:
                  extension.extensionPhone?.routeNoAnswerNullAction === NullActionTypes.Unset
                     ? {
                          extensionId: extension?.extensionPhone?.routeNoAnswerExtension?.extensionId ?? null,
                          name: extension.extensionPhone?.routeNoAnswerExtension?.name ?? '',
                          number: extension.extensionPhone?.routeNoAnswerExtension?.number,
                       }
                     : {
                          extensionId: null,
                          name: getNullActionPlaceholder(extension.extensionPhone?.routeNoAnswerNullAction),
                          number: getNullActionTypeNumericValue(
                             extension.extensionPhone?.routeNoAnswerNullAction ?? NullActionTypes.Unset
                          ),
                       },
               routeNoAnswerNullAction:
                  extension.extensionPhone?.routeNoAnswerExtension?.extensionId === -1
                     ? NullActionTypes.Directory
                     : extension.extensionPhone?.routeNoAnswerExtension?.extensionId === -2
                     ? NullActionTypes.Hangup
                     : extension.extensionPhone?.routeNoAnswerExtension?.extensionId === -3
                     ? NullActionTypes.Voicemail
                     : extension.extensionPhone?.routeNoAnswerNullAction ?? NullActionTypes.Unset,
               extensionSupervisees:
                  extension.extensionPhone?.extensionSuperviseesConnection?.items.map((item) => ({
                     supervisorExtensionId: item.supervisorExtensionId,
                     superviseeExtensionId: item.superviseeExtension.extensionId,
                     extensionSuperviseeId: item.extensionSuperviseeId,
                     name: item.superviseeExtension.name,
                     number: item.superviseeExtension.number,
                     extensionId: item.superviseeExtension.extensionId,
                  })) || [],
               extensionGroupSupervisees:
                  extension.extensionPhone?.extensionGroupSuperviseesConnection?.items.map((item) => ({
                     superviseeExtensionGroupId: item.superviseeExtensionGroup.extensionGroupId,
                     supervisorExtensionId: item.supervisorExtensionId,
                     extensionGroupSuperviseeId: item.extensionGroupSuperviseeId,
                     name: item.superviseeExtensionGroup.name,
                     description: item.superviseeExtensionGroup.description,
                  })) || [],
               extensionsOrExtensionGroupSupervisees: {
                  extensionSupervisees:
                     extension.extensionPhone?.extensionSuperviseesConnection?.items.map((item) => ({
                        supervisorExtensionId: item.supervisorExtensionId,
                        superviseeExtensionId: item.superviseeExtension.extensionId,
                        extensionSuperviseeId: item.extensionSuperviseeId,
                        name: item.superviseeExtension.name,
                        number: item.superviseeExtension.number,
                        extensionId: item.superviseeExtension.extensionId,
                     })) || [],
                  extensionGroupSupervisees:
                     extension.extensionPhone?.extensionGroupSuperviseesConnection?.items.map((item) => ({
                        superviseeExtensionGroupId: item.superviseeExtensionGroup.extensionGroupId,
                        supervisorExtensionId: item.supervisorExtensionId,
                        extensionGroupSuperviseeId: item.extensionGroupSuperviseeId,
                        name: item.superviseeExtensionGroup.name,
                        description: item.superviseeExtensionGroup.description,
                     })) || [],
               },
            };
            formValue.mailbox = {
               emailAddress: extension.mailbox?.emailAddress,
               discardAudioAfterEmail: extension.mailbox?.discardAudioAfterEmail,
               sayCallerId: extension.mailbox?.sayCallerId,
               markReadAfterEmail: extension.mailbox?.markReadAfterEmail,
               isInNameDirectory: extension.mailbox?.isInNameDirectory,
               unavailablePrompt: extension.mailbox?.unavailablePrompt,
               temporaryUnavailablePrompt: extension.mailbox?.temporaryUnavailablePrompt,
               useTemporaryGreeting: extension.mailbox?.temporaryUnavailablePromptId != null,
               mailboxId: extension.mailbox?.mailboxId,
               namePrompt: extension.mailbox?.namePrompt,
               operatorExtension: extension.mailbox?.mailboxOperatorExtension,
               password: extension.mailbox?.password ? '0000' : '',
               busyPrompt: extension.mailbox?.busyPrompt,
            };
            break;
         case ExtensionTypes.CallQueue:
            this.setExtensionQueueValidators();
            formValue.extensionQueue = {
               announcementFrequency: extension.extensionQueue?.announcementFrequency,
               callbackPrompt: {
                  name: extension.extensionQueue?.callbackPrompt?.name ?? '',
                  promptId: extension?.extensionQueue?.callbackPrompt?.promptId ?? null,
               },
               callersNeededForCallback: extension.extensionQueue?.callersNeededForCallback ?? 3,
               typeOfCallback: extension.extensionQueue?.callBackType ?? null,
               holdTimeNeededForCallback: extension.extensionQueue?.holdTimeNeededForCallback ?? 5,
               exitRouteToExtension: {
                  extensionId: extension?.extensionQueue?.exitRouteToExtension?.extensionId ?? null,
                  name: extension.extensionQueue?.exitRouteToExtension?.name ?? '',
                  number: extension.extensionQueue?.exitRouteToExtension?.number,
                  type: extension.extensionQueue?.exitRouteToExtension?.type,
               },
               exitRouteVoicemailOnly: extension.extensionQueue?.exitRouteVoicemailOnly,
               hasPreventEmptyQueue: extension.extensionQueue?.hasPreventEmptyQueue ?? false,
               queueEmptyExitRouteToExtension:
                  extension.extensionQueue?.queueEmptyExitNullAction === NullActionTypes.Unset
                     ? {
                          extensionId: extension?.extensionQueue?.queueEmptyExitRouteToExtension?.extensionId ?? null,
                          name: extension.extensionQueue?.queueEmptyExitRouteToExtension?.name ?? '',
                          number: extension.extensionQueue?.queueEmptyExitRouteToExtension?.number,
                       }
                     : {
                          extensionId: null,
                          name: getNullActionPlaceholder(extension.extensionQueue?.queueEmptyExitNullAction),
                          number: getNullActionTypeNumericValue(
                             extension.extensionQueue?.queueEmptyExitNullAction ?? NullActionTypes.Unset
                          ),
                       },
               queueEmptyExitNullAction: extension.extensionQueue?.queueEmptyExitNullAction,
               extensionQueueId: extension.extensionQueue?.extensionQueueId,
               holdMusicPrompt: {
                  name: extension.extensionQueue?.holdMusicPrompt?.name ?? '',
                  promptId: extension?.extensionQueue?.holdMusicPrompt?.promptId ?? null,
               },
               isAnnounceQueueCount: extension.extensionQueue?.isAnnounceQueueCount ?? false,
               isAnnounceCallback: extension.extensionQueue?.isAnnounceCallback ?? false,
               isAnnounceWaitTime: extension.extensionQueue?.isAnnounceWaitTime ?? false,
               isCallBackAvailable: extension.extensionQueue?.isCallBackAvailable ?? false,
               periodicAnnouncementFrequency: extension.extensionQueue?.periodicAnnouncementFrequency ?? null,
               queueMembers: extension.extensionQueue?.queueMembersConnection.items
                  .filter((queueMember) => queueMember.queueExtensionGroupId === null)
                  .map((queueMember) => ({
                     extensionId: queueMember.agentExtension.extensionId,
                     name: queueMember.agentExtension.name,
                     number: queueMember.agentExtension.number,
                     extensionQueueId: queueMember.extensionQueueId,
                     queueMemberId: queueMember.queueMemberId,
                     queueExtensionGroupId: queueMember.queueExtensionGroupId,
                  })),
               queueGroupExtensionGroups: extension.extensionQueue?.queueExtensionGroupMembersConnection.items.map(
                  (queueMember) => ({
                     queueExtensionGroupId: queueMember.queueExtensionGroupId,
                     queueExtensionId: queueMember.queueExtensionId,
                     extensionGroupId: queueMember.extensionGroupId,
                     extensionGroup: queueMember.extensionGroup,
                     name: queueMember.extensionGroup.name,
                  })
               ),
               queuePeriodicAnnouncements: extension.extensionQueue?.queuePeriodicAnnouncementsConnection.items,
               queueStrategyId: extension.extensionQueue?.queueStrategyId,
               wrapUpTime: extension.extensionQueue?.wrapUpTime ?? 60,
               extensionsOrExtensionQueueGroupMembers: {
                  queueMembers:
                     extension.extensionQueue?.queueMembersConnection.items
                        .filter((queueMember) => queueMember.queueExtensionGroupId === null)
                        .map((queueMember) => ({
                           extensionId: queueMember.agentExtension.extensionId,
                           name: queueMember.agentExtension.name,
                           number: queueMember.agentExtension.number,
                           extensionQueueId: queueMember.extensionQueueId,
                           queueMemberId: queueMember.queueMemberId,
                           queueExtensionGroupId: queueMember.queueExtensionGroupId,
                        })) || [],
                  queueExtensionGroupMembers:
                     extension.extensionQueue?.queueExtensionGroupMembersConnection?.items.map((item) => ({
                        queueExtensionGroupId: item.queueExtensionGroupId,
                        queueExtensionId: item.queueExtensionId,
                        extensionGroupId: item.extensionGroupId,
                        extensionGroup: item.extensionGroup,
                        name: item.extensionGroup.name,
                     })) || [],
               },
            };
            break;
         case ExtensionTypes.Fax:
            formValue.extensionFax = {
               defaultPhoneNumber: extension.extensionFax?.defaultPhoneNumber
                  ? {
                       displayName: extension.extensionFax.defaultPhoneNumber.displayName,
                       phoneNumber: extension.extensionFax.defaultPhoneNumber.phoneNumber,
                       phoneNumberId: extension.extensionFax.defaultPhoneNumber.phoneNumberId,
                    }
                  : null,
               extension: {
                  extensionId: extension.extensionFax?.extension?.extensionId,
                  name: extension.extensionFax?.extension?.name,
                  number: extension.extensionFax?.extension?.number,
               },
               emailAddress: extension.extensionFax?.emailAddress,
               extensionFaxId: extension.extensionFax?.extensionFaxId ?? null,
            };
            break;
         case ExtensionTypes.FindMeFollowMe:
            this.setFmFmValidators();
            formValue.extensionFindMeFollowMe = {
               extensionFmFmId: extension.extensionFmFm?.extensionFmFmId ?? null,
               extensionId: extension.extensionId,
               finalDestinationExtension: {
                  extensionId: extension.extensionFmFm?.finalDestinationExtension?.extensionId,
                  name: extension.extensionFmFm?.finalDestinationExtension?.name,
                  number: extension.extensionFmFm?.finalDestinationExtension?.number,
                  type: extension.extensionFmFm?.finalDestinationExtension?.type,
               },
               isFinalDestinationVoicemail: extension.extensionFmFm?.isFinalDestinationVoicemail,
               isIgnoreForward: extension.extensionFmFm?.isIgnoreForward,
               isGreetingDisabled: extension.extensionFmFm?.isGreetingDisabled,
               isSendOriginalCallerId: extension.extensionFmFm?.isSendOriginalCallerId,
               finalDestinationPhoneNumber: extension.extensionFmFm?.finalDestinationPhoneNumber ?? null,
               defaultPhoneNumber: extension.extensionFmFm?.defaultPhoneNumber
                  ? {
                       displayName: extension.extensionFmFm.defaultPhoneNumber.displayName,
                       phoneNumber: extension.extensionFmFm.defaultPhoneNumber.phoneNumber,
                       phoneNumberId: extension.extensionFmFm.defaultPhoneNumber.phoneNumberId,
                    }
                  : null,
               fmFmSteps: extension.extensionFmFm?.fmFmStepsConnection.items.map((step): FindMeFollowMeStepItemView => {
                  let stepOptions: ExtensionChipInputOption[] = [];
                  const extensionSteps = [...step.fmFmStepExtensionsConnection.items];
                  extensionSteps.sort((a, b) => {
                     return a.extension.number - b.extension.number;
                  });
                  stepOptions.push(
                     ...extensionSteps.map((stepExt): ExtensionChipInputOption => {
                        return {
                           stepId: stepExt.fmFmStepExtensionId,
                           type: ExtensionChipInputType.Extension,
                           value: stepExt.extension,
                           isDeleted: false,
                        };
                     })
                  );
                  const extensionPhoneNumberSteps = [...step.fmFmStepPhoneNumbersConnection.items];
                  extensionPhoneNumberSteps.sort((a, b) => {
                     return (a.phoneNumber ?? '').localeCompare(b.phoneNumber ?? '');
                  });
                  stepOptions.push(
                     ...extensionPhoneNumberSteps.map((stepPhoneNumber): ExtensionChipInputOption => {
                        return {
                           stepId: stepPhoneNumber.fmFmStepPhoneNumberId,
                           type: ExtensionChipInputType.PhoneNumber,
                           value: stepPhoneNumber.phoneNumber ?? '',
                           isDeleted: false,
                        };
                     })
                  );
                  return {
                     fmFmStepId: step.fmFmStepId,
                     timeout: step.timeout,
                     sequence: step.sequence,
                     isDeleted: false,
                     extensionsOrPhoneNumbersToDial: stepOptions,
                  };
               }),
            };
            break;
      }
      this.form.get('name')?.setValidators([Validators.required, containsCharactersValidator()]);
      this.form.get('shortName')?.setValidators([Validators.pattern(/^.{0,20}$/)]);
      this.form
         .get('number')
         ?.setValidators([
            Validators.required,
            Validators.min(100),
            Validators.max(99999),
            Validators.pattern(/^\s*\d+\s*$/),
            Validators.pattern(/^\s*(?!911)\d+\s*$/),
            Validators.pattern(/^\s*(?!933$)\d+\s*$/),
            Validators.pattern(/^\s*(?!(70[0-9]|7[1-9][0-9])$)\d+\s*$/),
         ]);
      this.form.get('type')?.setValidators(Validators.required);

      this.form.patchValue(formValue);
      this.markAsUntouchedAndPristine();
   }

   updateExtensionQueue(extensionQueue: GetExtensionDetail_extension_extensionQueue): void {
      const formValue: { [key: string]: any } = {};

      this.setExtensionQueueValidators();
      formValue.extensionQueue = {
         callbackPrompt: {
            name: extensionQueue?.callbackPrompt?.name ?? '',
            promptId: extensionQueue?.callbackPrompt?.promptId ?? null,
         },
         extensionQueueId: extensionQueue?.extensionQueueId,
         holdMusicPrompt: {
            name: extensionQueue?.holdMusicPrompt?.name ?? '',
            promptId: extensionQueue?.holdMusicPrompt?.promptId ?? null,
         },
         queuePeriodicAnnouncements: extensionQueue?.queuePeriodicAnnouncementsConnection.items,
      };
      this.form.patchValue(formValue);
      this.markAsUntouchedAndPristine();
   }

   getValue(extensionId: number | null): ExtensionDetailSaveModel {
      const value = this.form.value as ExtensionDetailFormValue;
      const extensionType = value?.type?.name ?? '';
      const extension: ExtensionDetailSaveModel = {
         extension: {
            accountId: value?.accountId ?? 0,
            extensionId: extensionId,
            //mailbox: value?.mailbox,
            locationId: value.location?.locationId ?? null,
            mailboxId: value?.mailboxId ?? null,
            adminNote: {
               noteId: value.adminNote?.noteId ?? 0,
               accountId: value.accountId ?? 0,
               extensionId: extensionId,
               notes: value.adminNotes ?? value.adminNote?.notes,
               isSystemNote: true,
               fields: [],
            },
            extensionNote: {
               noteId: value.extensionNote?.noteId ?? 0,
               accountId: value.accountId ?? 0,
               extensionId: extensionId,
               notes: value.extensionNotes ?? value.extensionNote?.notes,
               isSystemNote: false,
               fields: [],
            },
            name: value.name,
            shortName: value.shortName,
            number: value.number,
            timeBasedRoutes: value.timeBasedRoutes ?? null,
            timeZoneId: value.timeZone?.timeZoneId ?? null,
            type: ExtensionTypes[extensionType],
            users: value.users
               ? value.users.map((user) => {
                    return {
                       extensionId: extensionId,
                       userId: user.userId,
                    };
                 })
               : null,
            faxEmailAddresses: value.faxEmailAddresses
               ? value.faxEmailAddresses.map((email: FaxEmailItemView) => {
                    return {
                       ...email,
                       faxEmailAddress: email.emailAddress,
                       extensionId: email.extensionId ?? 0,
                       isDeleted: email.isDeleted ?? false,
                    };
                 })
               : null,
            departmentExtensions: value.departmentExtensions
               ? value.departmentExtensions.map((departmentExtension: DepartmentExtensionItemView) => {
                    return {
                       departmentExtensionId: departmentExtension.departmentExtensionId ?? null,
                       extensionId: departmentExtension.extensionId ?? null,
                       departmentId: departmentExtension.departmentId ?? null,
                       department: {
                          departmentId: departmentExtension.department?.departmentId ?? null,
                          name: departmentExtension.department?.name ?? null,
                          isActive: departmentExtension.department?.isActive ?? false,
                          accountId: departmentExtension.department?.accountId ?? null,
                       },
                       isDeleted: departmentExtension.isDeleted ?? false,
                    };
                 })
               : null,
         },
      };
      switch (extensionType) {
         case ExtensionTypes.Phone:
            extension.extension.extensionPhone = {
               defaultPhoneNumberId: value.extensionPhone?.defaultPhoneNumber?.phoneNumberId ?? null,
               dndStatus: value.extensionPhone?.dndStatus ?? null,
               displayName: value.extensionPhone?.displayName ?? extension.extension.name,
               extensionId: extensionId,
               defaultAreaCode: value.extensionPhone?.defaultAreaCode ?? null,
               extensionPhoneId: value.extensionPhone?.extensionPhoneId ?? null,
               forwardExternalNumber: value.extensionPhone?.forwardExternalNumber ?? null,
               forwardToExtensionId: value.extensionPhone?.forwardToExtension?.extensionId ?? null,
               phones: value.extensionPhone?.phones ?? null,
               ringTimeOut: value.extensionPhone?.ringTimeout ?? null,
               enableDtmf: value.extensionPhone?.enableDtmf ?? false,
               secondsToWait: value.extensionPhone?.secondsToWait ?? null,
               dtmfToPlay: value.extensionPhone?.dtmfToPlay ?? null,
               isOtrEnabled: value.extensionPhone?.isOtrEnabled ?? false,
               isAutoAnswer: value.extensionPhone?.isAutoAnswer ?? false,
               isRingAllPhones: value.extensionPhone?.isRingAllPhones ?? true,
               autoAnswerPhoneId: value.extensionPhone?.autoAnswerPhone?.phoneId ?? null,
               isAutoAnswerEnabled: value.extensionPhone?.isAutoAnswerEnabled ?? false,
               routeBusyExtensionId: value.extensionPhone?.routeBusyExtension?.extensionId ?? null,
               routeBusyNullAction: value.extensionPhone?.routeBusyNullAction ?? NullActionTypes.Unset,
               routeNoAnswerExtensionId: value.extensionPhone?.routeNoAnswerExtension?.extensionId ?? null,
               routeNoAnswerNullAction: value.extensionPhone?.routeNoAnswerNullAction ?? NullActionTypes.Unset,
               office: value.extensionPhone?.office ?? null,
               extensionSupervisees: [],
               extensionGroupSupervisees: [],
            };
            if (
               value.extensionPhone?.extensionsOrExtensionGroupSupervisees &&
               Array.isArray(value.extensionPhone?.extensionsOrExtensionGroupSupervisees)
            ) {
               value.extensionPhone.extensionsOrExtensionGroupSupervisees.forEach((item) => {
                  if (item.type === ChipInputType.Extension && item.isDeleted === false) {
                     extension.extension.extensionPhone?.extensionSupervisees?.push({
                        extensionSuperviseeId: item.extensionSuperviseeId,
                        supervisorExtensionId: extensionId,
                        superviseeExtensionId: item.extensionId,
                     });
                  } else if (item.type === ChipInputType.ExtensionGroup && item.isDeleted === false) {
                     extension.extension.extensionPhone?.extensionGroupSupervisees?.push({
                        extensionGroupSuperviseeId: item.extensionGroupSuperviseeId,
                        supervisorExtensionId: extensionId,
                        superviseeExtensionGroupId: item.extensionGroupId,
                     });
                  }
               });
            } else if (value.extensionPhone?.extensionSupervisees || value.extensionPhone?.extensionGroupSupervisees) {
               value.extensionPhone.extensionSupervisees.forEach((item) => {
                  extension.extension.extensionPhone?.extensionSupervisees?.push({
                     extensionSuperviseeId: item.extensionSuperviseeId,
                     supervisorExtensionId: extensionId,
                     superviseeExtensionId: item.extensionId,
                  });
               });
               value.extensionPhone.extensionGroupSupervisees.forEach((item) => {
                  extension.extension.extensionPhone?.extensionGroupSupervisees?.push({
                     extensionGroupSuperviseeId: item.extensionGroupSuperviseeId,
                     supervisorExtensionId: extensionId,
                     superviseeExtensionGroupId: item.superviseeExtensionGroupId,
                  });
               });
            }
            extension.extension.mailbox = {
               emailAddress: this.normalizeEmailList(value.mailbox?.emailAddress),
               discardAudioAfterEmail: value.mailbox?.discardAudioAfterEmail ?? false,
               markReadAfterEmail: value.mailbox?.markReadAfterEmail ?? false,
               sayCallerId: value.mailbox?.sayCallerId ?? true,
               isInNameDirectory: value.extensionPhone?.isInNameDirectory ?? false,
               unavailablePromptId2: value.mailbox?.unavailablePrompt?.promptId ?? null,
               temporaryUnavailablePromptId: value.mailbox?.useTemporaryGreeting
                  ? value.mailbox?.temporaryUnavailablePrompt?.promptId ?? null
                  : null,
               mailboxId: value.mailbox?.mailboxId ?? null,
               namePromptId: value.mailbox?.namePrompt?.promptId ?? null,
               operatorExtensionId: value.mailbox?.operatorExtension?.extensionId ?? null,
               password: value.mailbox?.password ?? null,
               busyPromptId: value.mailbox?.busyPrompt?.promptId ?? null,
            };
            break;
         case ExtensionTypes.PagingGroup:
            extension.extension.extensionPagingGroup = {
               extensionId: extensionId,
               extensionPagingGroupId: value.extensionPagingGroup?.extensionPagingGroupId ?? null,
               pagingGroupMembers: [],
               pagingGroupExtensionGroups: [],
            };
            const extensionPagingGroupId = value.extensionPagingGroup?.extensionPagingGroupId ?? null;
            if (
               value.extensionPagingGroup?.pagingGroupMembersOrExtensionGroupMembers &&
               Array.isArray(value.extensionPagingGroup?.pagingGroupMembersOrExtensionGroupMembers)
            ) {
               value.extensionPagingGroup.pagingGroupMembersOrExtensionGroupMembers.forEach((item) => {
                  if (item.type === ChipInputType.Extension && item.isDeleted === false) {
                     extension.extension.extensionPagingGroup?.pagingGroupMembers?.push({
                        pagingGroupMemberId: item.pagingGroupMemberId,
                        extensionPagingGroupId: extensionPagingGroupId,
                        extensionId: item.id,
                     });
                  } else if (item.type === ChipInputType.ExtensionGroup && item.isDeleted === false) {
                     extension.extension.extensionPagingGroup?.pagingGroupExtensionGroups?.push({
                        pagingGroupExtensionGroupId: item.pagingGroupExtensionGroupId || null,
                        extensionPagingGroupId: extensionPagingGroupId,
                        extensionGroupId: item.id,
                     });
                  }
               });
            } else if (
               value.extensionPagingGroup?.pagingGroupMembers ||
               value.extensionPagingGroup?.pagingGroupExtensionGroups
            ) {
               value.extensionPagingGroup.pagingGroupMembers.forEach((item) => {
                  extension.extension.extensionPagingGroup?.pagingGroupMembers?.push({
                     pagingGroupMemberId: item.pagingGroupMemberId,
                     extensionPagingGroupId: extensionPagingGroupId,
                     extensionId: item.extensionId,
                  });
               });
               value.extensionPagingGroup.pagingGroupExtensionGroups.forEach((item) => {
                  extension.extension.extensionPagingGroup?.pagingGroupExtensionGroups?.push({
                     pagingGroupExtensionGroupId: item.pagingGroupExtensionGroupId || null,
                     extensionPagingGroupId: extensionPagingGroupId,
                     extensionGroupId: item.extensionGroupId,
                  });
               });
            }
            break;
         case ExtensionTypes.AutoAttendant:
            extension.extension.extensionAutoAttendant = {
               autoAttendantKeys: value.extensionAutoAttendant?.autoAttendantKeys ?? null,
               autoAttendantPrompts: value.extensionAutoAttendant?.autoAttendantPrompts ?? null,
               extensionAutoAttendantId: value.extensionAutoAttendant?.extensionAutoAttendantId ?? null,
               extensionId: extensionId,
               useTimeoutToggle: value.extensionAutoAttendant?.useTimeoutToggle ?? false,
               timeoutExtensionId: value.extensionAutoAttendant?.timeoutExtension?.extensionId ?? null,
               timeoutSeconds: value.extensionAutoAttendant?.timeoutSeconds ?? null,
               firstPromptNotSkippable: value.extensionAutoAttendant?.firstPromptNotSkippable ?? false,
               timeoutNullAction:
                  value.extensionAutoAttendant?.timeoutExtension?.number === -1
                     ? NullActionTypes.Directory
                     : value.extensionAutoAttendant?.timeoutExtension?.number === -2
                     ? NullActionTypes.Hangup
                     : value.extensionAutoAttendant?.timeoutNullAction ?? NullActionTypes.Unset,
               isTimeoutVoicemailOnly: value.extensionAutoAttendant?.isTimeoutVoicemailOnly ?? false,
            };
            break;
         case ExtensionTypes.HuntGroup:
            extension.extension.extensionHuntGroup = {
               callerIdOverride: value.extensionHuntGroup?.callerIdOverride ?? false,
               displayName: value.extensionHuntGroup?.displayName ?? null,
               extensionHuntGroupId: value.extensionHuntGroup?.extensionHuntGroupId ?? null,
               extensionId: extensionId,
               ringTimeOut: value.extensionHuntGroup?.ringTimeout ?? null,
               routeBusyExtensionId: value.extensionHuntGroup?.routeBusyExtension?.extensionId,
               routeBusyNullAction: value.extensionHuntGroup?.routeBusyNullAction ?? NullActionTypes.Unset,
               routeNoAnswerExtensionId: value.extensionHuntGroup?.routeNoAnswerExtension?.extensionId,
               isRouteBusyVoicemailOnly: value.extensionHuntGroup?.isRouteBusyVoicemailOnly ?? false,
               isRouteNoAnswerVoicemailOnly: value.extensionHuntGroup?.isRouteNoAnswerVoicemailOnly ?? false,
               routeNoAnswerNullAction: value.extensionHuntGroup?.routeNoAnswerNullAction ?? NullActionTypes.Unset,
               huntGroupMembers: [],
               extensionHuntGroups: [],
            };
            const extensionHuntGroupId = value.extensionHuntGroup?.extensionHuntGroupId;
            if (
               value.extensionHuntGroup?.extensionsOrExtensionGroupMembers &&
               Array.isArray(value.extensionHuntGroup?.extensionsOrExtensionGroupMembers)
            ) {
               value.extensionHuntGroup.extensionsOrExtensionGroupMembers.forEach((item) => {
                  if (item.type === ChipInputType.Extension && item.isDeleted === false) {
                     extension.extension.extensionHuntGroup?.huntGroupMembers?.push({
                        huntGroupMemberId: item.huntGroupMemberId,
                        extensionHuntGroupId: extensionHuntGroupId ?? null,
                        extensionId: item.id,
                     });
                  } else if (item.type === ChipInputType.ExtensionGroup && item.isDeleted === false) {
                     extension.extension.extensionHuntGroup?.extensionHuntGroups?.push({
                        huntGroupExtensionGroupId: item.huntGroupExtensionGroupId ?? null,
                        huntGroupExtensionId: extensionHuntGroupId ?? null,
                        extensionGroupId: item.id,
                     });
                  }
               });
            } else if (
               value.extensionHuntGroup?.huntGroupMembers ||
               value.extensionHuntGroup?.huntGroupExtensionGroupMembers
            ) {
               value.extensionHuntGroup.huntGroupMembers.forEach((item) => {
                  extension.extension.extensionHuntGroup?.huntGroupMembers?.push({
                     huntGroupMemberId: item.huntGroupMemberId,
                     extensionHuntGroupId: extensionHuntGroupId ?? null,
                     extensionId: item.extensionId,
                  });
               });
               value.extensionHuntGroup.huntGroupExtensionGroupMembers.forEach((item) => {
                  extension.extension.extensionHuntGroup?.extensionHuntGroups?.push({
                     huntGroupExtensionGroupId: item.huntGroupExtensionGroupId ?? null,
                     huntGroupExtensionId: extensionHuntGroupId ?? null,
                     extensionGroupId: item.extensionGroupId,
                  });
               });
            }
            break;
         case ExtensionTypes.ExternalForward:
            extension.extension.extensionExternalForward = {
               dtmf: value.extensionExternalForward?.dtmf ?? null,
               extensionExternalForwardId: value.extensionExternalForward?.extensionExternalForwardId ?? null,
               extensionId: extensionId,
               forwardToNumber: trimPhoneNumber(value.extensionExternalForward?.forwardToNumber) ?? null,
               isDtmf: value.extensionExternalForward?.isDtmf ?? null,
               isPassthroughOriginalCallerId: value.extensionExternalForward?.isPassthroughOriginalCallerId ?? null,
               pauseSeconds: value.extensionExternalForward?.pauseSeconds ?? null,
            };
            break;
         case ExtensionTypes.SmartVoicemail:
         case ExtensionTypes.VoiceMailbox:
            extension.extension.extensionMailbox = {
               extensionId: extensionId,
               extensionMailboxId: value.extensionMailbox?.extensionMailboxId ?? null,
            };
            extension.extension.mailbox = {
               emailAddress: this.normalizeEmailList(value.mailbox?.emailAddress),
               discardAudioAfterEmail: value.mailbox?.discardAudioAfterEmail ?? false,
               markReadAfterEmail: value.mailbox?.markReadAfterEmail ?? false,
               isInNameDirectory: value.extensionMailbox?.isInNameDirectory ?? false,
               sayCallerId: value.mailbox?.sayCallerId ?? true,
               unavailablePromptId2: value.mailbox?.unavailablePrompt?.promptId ?? null,
               temporaryUnavailablePromptId: value.mailbox?.useTemporaryGreeting
                  ? value.mailbox?.temporaryUnavailablePrompt?.promptId ?? null
                  : null,
               mailboxId: value.mailbox?.mailboxId ?? null,
               namePromptId: value.mailbox?.namePrompt?.promptId ?? null,
               operatorExtensionId: value.mailbox?.operatorExtension?.extensionId ?? null,
               password: value.mailbox?.password ?? null,
               busyPromptId: value.mailbox?.busyPrompt?.promptId ?? null,
            };
            break;
         case ExtensionTypes.CallQueue:
            extension.extension.extensionQueue = {
               announcementFrequency: value.extensionQueue?.announcementFrequency ?? null,
               callersNeededForCallback: value.extensionQueue?.callersNeededForCallback ?? null,
               callBackType: value.extensionQueue?.typeOfCallback ?? null,
               holdTimeNeededForCallback: value.extensionQueue?.holdTimeNeededForCallback ?? null,
               customCallbackPromptId: value.extensionQueue?.callbackPrompt?.promptId ?? null,
               exitRouteTo: value.extensionQueue?.exitRouteToExtension?.extensionId ?? null,
               exitRouteVoicemailOnly: value.extensionQueue?.exitRouteVoicemailOnly ?? null,
               hasPreventEmptyQueue:
                  value.extensionQueue?.queueEmptyExitRouteToExtension !== null ||
                  value.extensionQueue?.queueEmptyExitNullAction !== NullActionTypes.Unset
                     ? true
                     : false,
               queueEmptyExitRouteTo: value.extensionQueue?.queueEmptyExitRouteToExtension?.extensionId ?? null,
               queueEmptyExitNullAction: value.extensionQueue?.queueEmptyExitNullAction ?? null,
               extensionId: extensionId,
               extensionQueueId: value.extensionQueue?.extensionQueueId ?? null,
               holdMusicPromptId: value.extensionQueue?.holdMusicPrompt?.promptId ?? null,
               isAnnounceQueueCount: value.extensionQueue?.isAnnounceQueueCount ?? null,
               isAnnounceCallback: value.extensionQueue?.isAnnounceCallback ?? null,
               isAnnounceWaitTime: value.extensionQueue?.isAnnounceWaitTime ?? null,
               isCallBackAvailable: value.extensionQueue?.isCallBackAvailable ?? null,
               periodicAnnouncementFrequency: value.extensionQueue?.periodicAnnouncementFrequency
                  ? value.extensionQueue?.periodicAnnouncementFrequency
                  : null,
               queuePeriodicAnnouncements: value.extensionQueue?.queuePeriodicAnnouncements ?? null,
               queueStrategyId: value.extensionQueue?.queueStrategyId ?? null,
               wrapUpTime: value.extensionQueue?.wrapUpTime ?? null,
               queueMembers: [],
               queueGroupMembers: [],
            };
            if (
               value.extensionQueue?.extensionsOrExtensionQueueGroupMembers &&
               Array.isArray(value.extensionQueue?.extensionsOrExtensionQueueGroupMembers)
            ) {
               value.extensionQueue.extensionsOrExtensionQueueGroupMembers.forEach((item) => {
                  if (item.type === ChipInputType.Extension && item.isDeleted === false) {
                     extension.extension.extensionQueue?.queueMembers?.push({
                        queueMemberId: item.value.queueMemberId,
                        agentExtensionId: item.extensionId,
                        extensionQueueId: value.extensionQueue?.extensionQueueId || null,
                     });
                  } else if (item.type === ChipInputType.ExtensionGroup && item.isDeleted === false) {
                     extension.extension.extensionQueue?.queueGroupMembers?.push({
                        queueExtensionGroupId: item.queueExtensionGroupId || null,
                        extensionQueueId: value.extensionQueue?.extensionQueueId || null,
                        extensionGroupId: item.extensionGroupId,
                     });
                  }
               });
            } else if (value.extensionQueue?.queueMembers || value.extensionQueue?.queueGroupExtensionGroups) {
               value.extensionQueue.queueMembers.forEach((item) => {
                  extension.extension.extensionQueue?.queueMembers?.push({
                     queueMemberId: item.queueMemberId,
                     agentExtensionId: item.extensionId,
                     extensionQueueId: value.extensionQueue?.extensionQueueId || null,
                  });
               });
               value.extensionQueue.queueGroupExtensionGroups.forEach((item) => {
                  extension.extension.extensionQueue?.queueGroupMembers?.push({
                     queueExtensionGroupId: item.queueExtensionGroupId || null,
                     extensionQueueId: value.extensionQueue?.extensionQueueId || null,
                     extensionGroupId: item.extensionGroupId,
                  });
               });
            }
            break;
         case ExtensionTypes.Fax:
            extension.extension.extensionFax = {
               defaultPhoneNumberId: value.extensionFax?.defaultPhoneNumber?.phoneNumberId ?? null,
               emailAddress: this.normalizeEmailList(value.extensionFax?.emailAddress),
               extensionId: value.extensionFax?.extension?.extensionId ?? null,
               extensionFaxId: value.extensionFax?.extensionFaxId ?? null,
               faxEmailAddresses: value.extensionFax?.faxEmailAddresses ?? null,
            };
            break;
         case ExtensionTypes.FindMeFollowMe:
            extension.extension.extensionFindMeFollowMe = {
               extensionFmFmId: value.extensionFindMeFollowMe?.extensionFmFmId ?? null,
               extensionFmFmSteps:
                  value.extensionFindMeFollowMe?.fmFmSteps?.map((step) => {
                     return {
                        extensionFmFmId: value.extensionFindMeFollowMe?.extensionFmFmId ?? null,
                        timeout: step.timeout,
                        sequence: step.sequence,
                        fmFmStepId: step.fmFmStepId,
                        fmFmStepExtensions: step.extensionsOrPhoneNumbersToDial
                           .filter((optionsToDial) => optionsToDial.type === ExtensionChipInputType.Extension)
                           .map((extensionsToDial) => {
                              return {
                                 fmFmStepExtensionId: extensionsToDial.stepId,
                                 fmFmStepId: step.fmFmStepId,
                                 extensionId: (extensionsToDial.value as ExtensionOption).extensionId,
                                 isDeleted: extensionsToDial.isDeleted ?? false,
                              };
                           }),
                        fmFmStepPhoneNumbers: step.extensionsOrPhoneNumbersToDial
                           .filter((optionsToDial) => optionsToDial.type === ExtensionChipInputType.PhoneNumber)
                           .map((phoneNumbersToDial) => {
                              return {
                                 fmFmStepPhoneNumberId: phoneNumbersToDial.stepId,
                                 fmFmStepId: step.fmFmStepId,
                                 phoneNumber: trimPhoneNumber(phoneNumbersToDial.value as string),
                                 isDeleted: phoneNumbersToDial.isDeleted ?? false,
                              };
                           }),
                        isDeleted: step.isDeleted,
                     };
                  }) ?? null,
               extensionId: extensionId,
               finalDestinationExtensionId:
                  value.extensionFindMeFollowMe?.finalDestinationExtension?.extensionId ?? null,
               isFinalDestinationVoicemail: value.extensionFindMeFollowMe?.isFinalDestinationVoicemail ?? false,
               isIgnoreForward: value.extensionFindMeFollowMe?.isIgnoreForward ?? false,
               isGreetingDisabled: value.extensionFindMeFollowMe?.isGreetingDisabled ?? false,
               isSendOriginalCallerId: value.extensionFindMeFollowMe?.isSendOriginalCallerId ?? false,
               finalDestinationPhoneNumber: trimPhoneNumber(value.extensionFindMeFollowMe?.finalDestinationPhoneNumber),
               defaultPhoneNumberId: value.extensionFindMeFollowMe?.defaultPhoneNumber?.phoneNumberId ?? null,
            };
            break;

         default:
            break;
      }
      return extension;
   }

   private normalizeEmailList(value: string | null | undefined): string | null {
      if (value == null) return null;

      const normalized = value
         .split(',')
         .map((email) => email.trim())
         .filter((email) => email.length > 0)
         .join(',');

      return normalized.length ? normalized : null;
   }

   addUniqueExtensionNumberValidator(extNumbers: number[]): void {
      const numberControl = this.form.get('number');
      if (numberControl) {
         const existingValidators = numberControl.validator ? [numberControl.validator] : [];
         numberControl.setValidators([...existingValidators, this.uniqueExtensionNumberValidator(extNumbers)]);
      }
   }

   uniqueSipUserNameValidator(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         const sipUserNames = control.value.flat();
         const hasDuplicates = sipUserNames.some((sipUserName, index) => {
            return sipUserNames.indexOf(sipUserName) !== index;
         });
         return hasDuplicates ? { mismatch: true } : null;
      };
   }

   uniqueExtensionNumberValidator(extNumbers: number[]): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         if (control.value === null || control.value === undefined) {
            return null;
         }
         const value = Number(('' + control.value).trim());
         const isDuplicate = extNumbers.includes(value);
         return isDuplicate ? { extensionAlreadyExists: true } : null;
      };
   }

   private setExtensionQueueValidators(): void {
      this.form.get('extensionQueue.queueStrategyId')?.setValidators(Validators.required);
      this.form
         .get('extensionQueue.wrapUpTime')
         ?.setValidators([Validators.required, Validators.min(0), Validators.max(600), Validators.pattern(/^\d+$/)]);
      this.form
         .get('extensionQueue.announcementFrequency')
         ?.setValidators([Validators.min(1), Validators.max(10), Validators.pattern(/^\d+$/)]);
      this.form
         .get('extensionQueue.callersNeededForCallback')
         ?.setValidators([Validators.required, Validators.min(3), Validators.max(30), Validators.pattern(/^\d+$/)]);
      this.form
         .get('extensionQueue.periodicAnnouncementFrequency')
         ?.setValidators([Validators.min(1), Validators.max(10), Validators.pattern(/^\d+$/)]);
      this.form.get('extensionQueue.typeOfCallback')?.setValidators(Validators.required);
      this.form
         .get('extensionQueue.holdTimeNeededForCallback')
         ?.setValidators([Validators.required, Validators.min(5), Validators.max(60), Validators.pattern(/^\d+$/)]);
   }

   private setVoicemailboxValidators(): void {
      this.form.get('extensionMailbox.notes')?.setValidators([Validators.maxLength(50)]);
      this.form.get('extensionMailbox.customerNotes')?.setValidators([Validators.maxLength(50)]);
   }

   private setHuntGroupValidators(): void {
      this.form
         .get('extensionHuntGroup.ringTimeout')
         ?.setValidators([Validators.required, Validators.min(0), Validators.max(59)]);
      this.form.get('extensionHuntGroup.displayName')?.setValidators(containsCharactersValidator());
   }

   private setExternalForwardValidators(): void {
      this.form
         .get('extensionExternalForward.dtmf')
         ?.setValidators([Validators.maxLength(25), Validators.pattern(/^\s*[\d*w#]{0,25}\s*$/)]);
      this.form.get('extensionExternalForward.forwardToNumber')?.setValidators(phoneNumberValidator());
      this.form.get('extensionExternalForward.pauseSeconds')?.setValidators([Validators.min(0), Validators.max(25)]);
   }

   private setFmFmValidators(): void {
      this.form.get('extensionFindMeFollowMe.defaultPhoneNumber')?.setValidators(Validators.required);
   }

   private setPhoneValidators(): void {
      this.form
         .get('extensionPhone.ringTimeout')
         ?.setValidators([Validators.required, Validators.min(0), Validators.max(59), phoneIntegerValidator()]);
      this.form.get('extensionPhone.displayName')?.setValidators(containsCharactersValidator());
   }

   private setAutoAttendantValidators(): void {
      this.form
         .get('extensionAutoAttendant.timeoutSeconds')
         ?.setValidators([Validators.required, Validators.min(1), Validators.max(10)]);
      this.form.get('extensionAutoAttendant.timeoutExtension')?.setValidators([Validators.required]);
      this.form.get('extensionAutoAttendant.autoAttendantKeys')?.setValidators(this.autoAttendantKeysValidator);
   }

   timeoutExtensionValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
      const useTimeout = group.get('useTimeoutToggle')?.value;
      const timeoutExtensionControl = group.get('timeoutExtension');
      const nullActionControl = group.get('timeoutNullAction');

      if (!useTimeout) {
         timeoutExtensionControl?.setErrors(null);
         return null;
      }

      const timeoutExtensionValue = timeoutExtensionControl?.value;
      const nullAction = nullActionControl?.value;

      const hasTimeoutExtension = !!timeoutExtensionValue?.extensionId;
      const hasValidNullAction = nullAction !== NullActionTypes.Unset;

      if (!hasTimeoutExtension && !hasValidNullAction) {
         timeoutExtensionControl?.setErrors({ required: true });
         return { required: true };
      }
      if (timeoutExtensionControl?.hasError('required')) {
         const errors = { ...timeoutExtensionControl.errors };
         delete errors['required'];
         timeoutExtensionControl.setErrors(Object.keys(errors).length ? errors : null);
      }

      return null;
   };

   queueEmptyExitValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
      const prevent = group.get('hasPreventEmptyQueue')?.value;
      const routeControl = group.get('queueEmptyExitRouteToExtension');
      const nullActionControl = group.get('queueEmptyExitNullAction');

      if (!prevent) {
         routeControl?.setErrors(null);
         nullActionControl?.setErrors(null);
         return null;
      }

      const route = routeControl?.value;
      const nullAction = nullActionControl?.value;

      const hasRoute = !!route?.extensionId;
      const hasValidNullAction = nullAction && nullAction !== NullActionTypes.Unset;

      if (!hasRoute && !hasValidNullAction) {
         routeControl?.setErrors({ queueEmptyExitRequired: true });
         return { queueEmptyExitRequired: true };
      }
      if (routeControl?.hasError('queueEmptyExitRequired')) {
         const errors = { ...routeControl.errors };
         delete errors['queueEmptyExitRequired'];
         routeControl.setErrors(Object.keys(errors).length ? errors : null);
      }

      return null;
   };

   autoAttendantKeysValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
      const keys: AutoAttendantKeyItemView[] = control.value ?? [];
      const activeKeys = keys.filter((k) => !k.isDeleted);

      if (activeKeys.some((k) => !k.keyNumber)) {
         return { required: true };
      }

      const keyNumbers = activeKeys.map((k) => k.keyNumber);
      const hasDuplicates = keyNumbers.some((num, i) => keyNumbers.indexOf(num) !== i);
      if (hasDuplicates) {
         return { duplicateKeys: true };
      }

      const hasInvalidRoutes = activeKeys.some(
         (k) => !k.routeToExtension?.extensionId && k.routeToNullAction === NullActionTypes.Unset
      );
      if (hasInvalidRoutes) {
         return { invalidKeys: true };
      }

      return null;
   };
}
